/**
 * ONE tab stop over a grid of cells, and the arrows that move it.
 *
 * Promoted out of `features/plan-progression/lib/matrixGridInteraction` on 17 September 2026, on its
 * second real consumer: the progression sheet's cycle matrix and `/raporty`'s client × survey grid.
 * Both are a rectangle of controls where every cell carrying its own tab stop is the defect — 52 × 20
 * cells is 1040 presses to reach the save button, and 8 × 15 is 120 to reach the note under the card.
 *
 * ── It owns the POSITION, never the rendering ────────────────────────────────────────────────
 * The caller draws whatever a cell is — a checkbox, a button, a number — and marks it
 * `data-cell="<row>-<column>"` with `tabIndex` 0 on the focused one and −1 everywhere else. This
 * module answers one question: which address has the stop, and where does a key move it.
 *
 * ── The grid's element is passed IN ──────────────────────────────────────────────────────────
 * `gridRef` is the caller's, not a ref this returns. The React Compiler refuses a ref written during
 * render, which is exactly what `ref={hook.ref}` compiles to.
 *
 * ── Focus is moved SYNCHRONOUSLY ─────────────────────────────────────────────────────────────
 * Not in an effect and not after a frame. Every cell is mounted whether or not it holds the stop and
 * `tabIndex={-1}` does not refuse a programmatic `focus()`, so there is nothing to wait for — and a
 * frame with no focus in it is a frame a surrounding focus trap claims for itself, which is how the
 * progression sheet lost the trainer's place in the grid and stopped responding to the arrows.
 */

import { useState, type KeyboardEvent, type RefObject } from 'react';

/** Which cell has the grid's one tab stop. Row and column are POSITIONS, not wire coordinates. */
export interface GridFocus {
  readonly row: number;
  readonly column: number;
}

export interface GridFocusNavigation {
  readonly focus: GridFocus;
  /** A cell reporting that the pointer put the tab stop on it. */
  readonly onCellFocus: (row: number, column: number) => void;
  /** Put the browser's focus on a cell — after a commit, a cancel, or an arrow. */
  readonly focusCell: (row: number, column: number) => void;
  readonly onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

export function useGridFocus(input: {
  /** The element the cells live under. Only its `data-cell` descendants are ever queried. */
  readonly gridRef: RefObject<HTMLElement | null>;
  readonly rowCount: number;
  readonly columnCount: number;
  /**
   * While something else owns the arrows — a cell holding an open field — this hook answers no keys
   * at all. Omitted where nothing can take them.
   */
  readonly disabled?: boolean;
}): GridFocusNavigation {
  const { gridRef, rowCount, columnCount, disabled = false } = input;
  const [held, setFocus] = useState<GridFocus>({ row: 0, column: 0 });
  // The held position is clamped on READ, not only on a move: a grid that shrinks under it (a start
  // date that spans fewer weeks) would otherwise leave no cell matching the stop, and the whole
  // grid would drop out of the tab order until something moved the position back inside.
  const focus: GridFocus = {
    row: Math.min(Math.max(0, rowCount - 1), held.row),
    column: Math.min(Math.max(0, columnCount - 1), held.column),
  };

  const focusCell = (row: number, column: number) => {
    gridRef.current
      ?.querySelector<HTMLElement>(`[data-cell="${String(row)}-${String(column)}"]`)
      ?.focus();
  };

  const moveFocus = (next: GridFocus) => {
    const row = Math.min(rowCount - 1, Math.max(0, next.row));
    const column = Math.min(columnCount - 1, Math.max(0, next.column));
    setFocus({ row, column });
    focusCell(row, column);
  };

  /**
   * Bind it in the CAPTURE phase wherever a cell is also a popup trigger.
   *
   * Base UI opens a menu on ArrowDown / ArrowUp (`openOnArrowKeyDown` in `MenuRoot`). Handled in the
   * bubble phase this runs AFTER that, so an arrow on such a cell both opens the menu and moves the
   * grid's focus out from under it. Capturing and stopping the arrows leaves them to the grid and
   * leaves Enter, Space and typeahead to the trigger.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (disabled) return;
    // Half a screen of columns, so a wide grid is crossed in presses rather than in dozens.
    const jump = Math.max(1, Math.floor(columnCount / 2));
    // Ctrl (or ⌘) turns Home and End into the GRID's corners rather than the row's ends — the
    // published grid pattern, and the only way out of the far corner of a 15-column table that is
    // not fifteen presses.
    const toCorner = event.ctrlKey || event.metaKey;
    switch (event.key) {
      case 'ArrowRight':
        moveFocus({ ...focus, column: focus.column + 1 });
        break;
      case 'ArrowLeft':
        moveFocus({ ...focus, column: focus.column - 1 });
        break;
      case 'ArrowDown':
        moveFocus({ ...focus, row: focus.row + 1 });
        break;
      case 'ArrowUp':
        moveFocus({ ...focus, row: focus.row - 1 });
        break;
      case 'Home':
        moveFocus({ row: toCorner ? 0 : focus.row, column: 0 });
        break;
      case 'End':
        moveFocus({ row: toCorner ? rowCount - 1 : focus.row, column: columnCount - 1 });
        break;
      case 'PageDown':
        moveFocus({ ...focus, column: focus.column + jump });
        break;
      case 'PageUp':
        moveFocus({ ...focus, column: focus.column - jump });
        break;
      default:
        return;
    }
    event.preventDefault();
    event.stopPropagation();
  };

  return {
    focus,
    onCellFocus: (row, column) => {
      setFocus({ row, column });
    },
    focusCell,
    onKeyDown,
  };
}
